import Autocomplete from '@mui/material/Autocomplete'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { AppTextField } from 'src/feature/form/component/analog/AppTextField'
import { AppCheckbox } from 'src/feature/form/component/analog/AppCheckbox'
import { useMyTeamStore } from '../../store/my-team.store'
import { useEffect, useState } from 'react'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

export type TeamSelectorProps = {
  showOptionForAll?: boolean
  onChange: (ids: string[]) => void
  defaultValues?: string[]
  type: 'team' | 'role'
}

type Option = {
  id: string
  name: string
  levelType: string
}

export default function TeamSelector({ showOptionForAll = false, onChange, defaultValues = [], type = 'role' }: TeamSelectorProps) {
  const myTeamStore = useMyTeamStore()
  const [options, setOptions] = useState<Option[]>([])
  const [selectedValue, setSelectedValue] = useState<Option | null>(null)

  useEffect(() => {
    const fetchAndSetOptions = async () => {
      await myTeamStore.getOrganisationOverview()
  
      if (myTeamStore.organisationOverview && myTeamStore.organisationOverview.length > 0) {
        const filteredOptions = myTeamStore.organisationOverview.filter(level => level.levelType === type)
  
        const allOptions = showOptionForAll
          ? [{ id: 'select-all', name: 'Select all', levelType: type }, ...filteredOptions]
          : filteredOptions
  
        setOptions(allOptions)
  
        // Set default selection
        if (showOptionForAll && defaultValues.includes('select-all')) {
          setSelectedValue(allOptions.find(option => option.id === 'select-all') || null)
          onChange(myTeamStore.organisationOverview.filter(level => level.levelType === type).map(level => level.id))
        } else if (defaultValues.length) {
          const defaultOption = allOptions.find(option => defaultValues.includes(option.id)) || null
          setSelectedValue(defaultOption)
          if (defaultOption) {
            onChange([defaultOption.id])
          }
        }
      } else {
        setOptions(showOptionForAll ? [{ id: 'select-all', name: 'Select all', levelType: type }] : [])
      }
    }
  
    fetchAndSetOptions()
  }, [])

  const handleChange = (_: unknown, value: Option | null) => {
    if (showOptionForAll && value?.id === 'select-all') {
      onChange(myTeamStore.organisationOverview.filter(level => level.levelType === type).map(level => level.id))
      setSelectedValue(value)
    } else {
      onChange(value ? [value.id] : [])
      setSelectedValue(value || null)
    }
  }

  return (
    <Autocomplete
      id="team-selector"
      size="small"
      sx={{
        '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
          color: 'text.primary',
        },
        width: { xs: '100%', md: '20%' },
      }}
      options={options}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={selectedValue}
      onChange={handleChange}
      getOptionLabel={option => option.name}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <AppCheckbox
            icon={icon}
            checkedIcon={checkedIcon}
            checked={selected}
            size="small"
          />
          {option.name}
        </li>
      )}
      renderInput={params => (
        <AppTextField
          {...params}
          placeholder={`select one ${type}`}
          sx={{
            fontSize: 12,
            m: 0,
          }}
          InputProps={{
            ...params.InputProps,
          }}
        />
      )}
    />
  )
}